import * as React from 'react';
import KnowledgeService from '@lifeomic/phc-web-toolkit/src/services/KnowledgeService';
import urls from '../../../../../src/util/urls';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { defineMessages, useIntl } from 'react-intl';
import { filterOptions as filterConfigOptions } from './filterOptions';
import { reduceFilters } from '../../FilterResultsManager/utils';
import { InfiniteScroll } from '@lifeomic/chroma-react/components/InfiniteScroll';
import { KnowledgeFilterLayout } from '../KnowledgeFilterLayout';
import { Link } from '@lifeomic/chroma-react/components/Link';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { useEffectOnce, useUpdateEffect } from 'react-use';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks';
import { usePagination } from '@lifeomic/phc-web-toolkit/src/services/util/usePagination';
import { JaxTypeahead } from './filterOptions/JaxTypeahead';
import { Text } from '@lifeomic/chroma-react/components/Text';

const knowledgeService = new KnowledgeService();

const useStyles = makeStyles(theme => ({
  resultsContentBox: {
    boxShadow: theme.boxShadows.table,
    maxHeight: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
  },
  descriptionCell: {
    maxWidth: theme.pxToRem(400),
    whiteSpace: 'normal',
  },
  caption: {
    marginTop: theme.spacing(1),
  },
  pmid: {
    '&::after': {
      content: "'|'",
    },

    '&:last-of-type': {
      '&::after': {
        content: "''",
      },
    },
  },
}));

const messages = defineMessages({
  copyright: {
    id: 'omicsExplorer.variantDetails.jaxCancerEffectContent.copyright',
    defaultMessage:
      '©Somatic gene variant annotations and related content have been powered by The Jackson Laboratory Clinical Knowledgebase (JAX-CKB™)',
  },
  variant: {
    id: 'omicsExplorer.variantDetails.jaxCancerEffectContent.variant',
    defaultMessage: 'Variant',
  },
  impact: {
    id: 'omicsExplorer.variantDetails.jaxCancerEffectContent.impact',
    defaultMessage: 'Variant Class',
  },
  proteinEffect: {
    id: 'omicsExplorer.variantDetails.jaxCancerEffectContent.proteinEffect',
    defaultMessage: 'Protein Effect',
  },
  variantDescription: {
    id: 'omicsExplorer.variantDetails.jaxCancerEffectContent.variantDescription',
    defaultMessage: 'Variant Description',
  },
  pubMedId: {
    id: 'omicsExplorer.variantDetails.jaxCancerEffectContent.pubMedId',
    defaultMessage: 'PMID',
  },
  error: {
    id: 'omicsExplorer.variantDetails.jaxCancerEffectContent.error',
    defaultMessage: 'There was an error with the request.',
  },
  accountError: {
    id: 'omicsExplorer.variantDetails.jaxCancerEffectContent.accountError',
    defaultMessage:
      'Account not authorized to view this content. Contact LifeOmic for access.',
  },
  emptyTableFiltersMsg: {
    id: 'omicsExplorer.variantDetails.jaxCancerEffectContent.emptyTableFiltersMsg',
    defaultMessage: 'No results, try removing filters.',
  },
  emptyTableNoFiltersMsg: {
    id: 'omicsExplorer.variantDetails.jaxCancerEffectContent.emptyTableNoFiltersMsg',
    defaultMessage: 'No variants associated with this gene.',
  },
});

const getTableConfig = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  classes, // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  formatMessage,
}) => [
  {
    header: { label: formatMessage(messages.variant) },
    cell: {
      content: (data: any) => data.variant,
    },
  },
  {
    header: { label: formatMessage(messages.impact) },
    cell: {
      content: (data: any) => data.impact,
    },
  },
  {
    header: { label: formatMessage(messages.proteinEffect) },
    cell: {
      content: (data: any) => data.protein_effect,
    },
  },
  {
    header: { label: formatMessage(messages.variantDescription) },
    cell: {
      content: (data: any) => (
        <div className={classes.descriptionCell}>
          {data.variant_description}
        </div>
      ),
    },
  },
  {
    header: { label: formatMessage(messages.pubMedId) },
    cell: {
      content: (data: any) => {
        const pmid = data.pub_med_id;
        if (!pmid) {
          return null;
        }

        return pmid.split('|')?.map((id: any) => (
          <Link
            className={classes.pmid}
            key={id}
            newTab
            to={urls.ncbi.pubmed(id)}
          >
            {id}
          </Link>
        ));
      },
    },
  },
];

const getFilterOptions = (gene: string) => [
  {
    content: JaxTypeahead,
    id: 'jaxCancerEffectContent.filters.variant',
    injectedProps: {
      gene,
      id: 'jaxCancerEffectContent.filters.variant',
      searchKey: 'variant',
      service: knowledgeService.getBiochemistry,
    },
    title: {
      id: 'jaxCancerEffectContent.filters.variant',
      defaultMessage: 'Variant',
    },
  },
  ...filterConfigOptions,
];

function getEmptyTableMsg(
  selectedFilterOptions: any,
  formatMessage: any,
): string {
  return selectedFilterOptions.length === 0
    ? formatMessage(messages.emptyTableNoFiltersMsg)
    : formatMessage(messages.emptyTableFiltersMsg);
}

export const JaxCancerEffectContent = ({
  gene,
  aaChanges,
}: {
  gene: string;
  aaChanges?: string[];
}) => {
  const [isLocalStateLoading, setIsLocalStateLoading] = React.useState(false);
  const [selectedFilterOptions, setSelectedFilterOptions] = React.useState([]);
  const filterOptions = getFilterOptions(gene);

  const {
    error,
    hasMoreToLoad,
    isLoading,
    loadMore,
    makeFetchRequest,
    results,
  } = usePagination(knowledgeService.getBiochemistry);

  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const [addNote] = useNotify();

  useEffectOnce(() => {
    if (!gene) {
      return;
    }

    if (aaChanges) {
      const aaFilters = [...new Set(aaChanges)].map(aaChange => {
        return {
          id: `jaxCancerEffectContent.filters.variant+${aaChange}`,
          origin: 'Variant',
          searchKey: 'variant',
          searchValue: aaChange,
        };
      });
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      setSelectedFilterOptions(aaFilters);
    } else {
      makeFetchRequest({ gene, pageSize: 50 });
    }
  });

  useUpdateEffect(() => {
    if (!gene) {
      return;
    }

    const filters = reduceFilters(selectedFilterOptions);
    setIsLocalStateLoading(true);

    const debouncedFetch = setTimeout(() => {
      makeFetchRequest({ filters, gene, pageSize: 50 });
      setIsLocalStateLoading(false);
    }, 500);

    return () => {
      clearTimeout(debouncedFetch);
      setIsLocalStateLoading(false);
    };
  }, [selectedFilterOptions]);

  React.useEffect(() => {
    const errorStatus = error?.response?.status;

    if (errorStatus) {
      if (errorStatus === 402) {
        addNote(formatMessage(messages.accountError), 'error');
        return;
      }
      addNote(formatMessage(messages.error), 'error');
    }
  }, [addNote, error, formatMessage]);

  return (
    <Box align="center" direction="column">
      <KnowledgeFilterLayout
        filterOptions={filterOptions}
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
      >
        <div className={classes.resultsContentBox}>
          <InfiniteScroll
            hasNextPage={hasMoreToLoad}
            loading={isLocalStateLoading || isLoading}
            loadMore={loadMore}
            scrollOffset={1000}
            scrollContainer="parent"
          >
            <TableModule
              config={getTableConfig({ classes, formatMessage })}
              data={results}
              data-testid="jax-cancer-effect-table"
              isLoading={isLocalStateLoading || isLoading}
              noResultsMessage={getEmptyTableMsg(
                selectedFilterOptions,
                formatMessage,
              )}
            />
          </InfiniteScroll>
        </div>
      </KnowledgeFilterLayout>
      <Text className={classes.caption} size="caption">
        {formatMessage(messages.copyright)}
      </Text>
    </Box>
  );
};
